import {
  Component,
  Input,
  forwardRef,
  signal,
  booleanAttribute,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type FormFieldType = 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'textarea' | 'select';
export type FormFieldLabelPlacement = 'floating' | 'above';

export interface FormFieldOption {
  label: string;
  value: unknown;
}

let uniqueId = 0;

/**
 * Shared, tenant-branded form control. Replaces PrimeNG inputs for simple
 * fields. Implements ControlValueAccessor so it works with both
 * `formControlName`/`formControl` and `[(ngModel)]`.
 *
 * Shortcut usage:
 *   <app-form-field formControlName="email" type="email" label="Email" />
 *   <app-form-field formControlName="bio" type="textarea" label="About you" [rows]="4" />
 *   <app-form-field formControlName="role" type="select" label="Role" [options]="roles" />
 *
 * Styling: floating label, tenant-accent focus ring, dark-mode aware. No PrimeNG.
 */
@Component({
  selector: 'app-form-field',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormFieldComponent),
      multi: true,
    },
  ],
})
export class FormFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() labelPlacement: FormFieldLabelPlacement = 'floating';
  @Input() type: FormFieldType = 'text';
  @Input() placeholder = '';
  @Input() autocomplete = 'off';
  @Input() rows = 3;
  @Input() options: FormFieldOption[] = [];
  /** Shown in red under the field when truthy. */
  @Input() error: string | null = null;
  /** Shown in muted text under the field (when no error). */
  @Input() hint = '';
  @Input({ transform: booleanAttribute }) required = false;
  /** Red border without a message (use when the message is shown elsewhere). */
  @Input({ transform: booleanAttribute }) invalid = false;

  readonly id = `ff-${uniqueId++}`;
  readonly value = signal<unknown>('');
  readonly disabled = signal(false);
  readonly focused = signal(false);
  readonly showPassword = signal(false);

  private onChange: (value: unknown) => void = () => {};
  private onTouched: () => void = () => {};

  /** Effective <input> type (password toggle swaps password<->text). */
  get inputType(): string {
    if (this.type === 'password') {
      return this.showPassword() ? 'text' : 'password';
    }
    return this.type;
  }

  get hasValue(): boolean {
    const v = this.value();
    return v !== null && v !== undefined && v !== '';
  }

  // --- ControlValueAccessor ---
  writeValue(value: unknown): void {
    this.value.set(value ?? '');
  }
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // --- events ---
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const rawValue = target.value;
    const nextValue = this.type === 'number' && rawValue !== '' ? Number(rawValue) : rawValue;
    this.value.set(nextValue);
    this.onChange(nextValue);
  }

  onBlur(): void {
    this.focused.set(false);
    this.onTouched();
  }

  onFocus(): void {
    this.focused.set(true);
  }

  togglePassword(): void {
    this.showPassword.update((v) => !v);
  }
}
